import { Box, BoxT, ModelBase, Point } from "pytha";
import { graphicStore } from "../store/graphic-store";
import { Box3, Box3Helper, BoxGeometry, BufferGeometry, Color, DoubleSide, EdgesGeometry, Float32BufferAttribute, Group, LineBasicMaterial, LineSegments, Matrix4, Mesh, MeshBasicMaterial, OrthographicCamera, Vector3 } from "three";

// 获取多个图元的boundingBox
export const getEntitiesBoundingBox = (models: ModelBase[], projectCamera: boolean) => {
    if (!models || models.length == 0) {
        return;
    }
    let x1 = +Infinity
    let y1 = +Infinity
    let z1 = +Infinity
    let x2 = -Infinity
    let y2 = -Infinity
    let z2 = -Infinity

    models.forEach(model => {
        if(model.type !== 'DTextT') {
            let box = model.getBoundingBox();
            if (projectCamera) {
                box.min.project(graphicStore.extraContext.getCurrentViewEditor().camera);
                box.max.project(graphicStore.extraContext.getCurrentViewEditor().camera);
            }
            x1 = Math.min(box.min.x, x1);
            y1 = Math.min(box.min.y, y1);
            z1 = Math.min(box.min.z, z1);
            x2 = Math.max(box.max.x, x2);
            y2 = Math.max(box.max.y, y2);
            z2 = Math.max(box.max.z, z2);
        }
    })

    if (x1 == null || y1 == null || x2 == null || y2 == null || z1 == null || z2 == null) {
        return null;
    }
    return {
        x1: x1,
        y1: y1,
        z1: z1,
        x2: x2,
        y2: y2,
        z2: z2,
    }
}

// 相机视角能够显示所有传入图元
export const showEntities = (models: ModelBase[]) => {
    if (!models || models.length == 0) {
        return;
    }

    let boundingBoxObj = getEntitiesBoundingBox(models, true);
    if (!boundingBoxObj) return;

    let x1 = boundingBoxObj.x1;
    let y1 = boundingBoxObj.y1;
    let x2 = boundingBoxObj.x2;
    let y2 = boundingBoxObj.y2;

    let scale1 = Math.abs(y1 - y2) / 2;

    let scale2 = Math.abs(x1 - x2) / 2;
    // max
    let scale = Math.max(scale1, scale2) * 2;
    let x = (x1 + x2) / 2;
    let y = (y1 + y2) / 2;
    let camera = graphicStore.extraContext.getCurrentViewEditor().camera;
    let target = graphicStore.context.ucsContext.unprojectCursorPoint(x, y, camera);

    let lastTarget = graphicStore.extraContext.getCurrentViewEditor().viewControl.target.clone();
    let offset = camera.position.clone().sub(lastTarget);
    const position = camera.position;
    graphicStore.extraContext.getCurrentViewEditor().viewControl.target.copy(target);
    position.copy(target).add(offset);
    graphicStore.extraContext.getCurrentViewEditor().camera.lookAt(target);
    // scale 框和当前边界的比例
    graphicStore.context.renderContext.cameraEdge *= scale;
    if (camera.type === "OrthographicCamera") {
        let orthoCamera = camera as OrthographicCamera;
        orthoCamera.left = orthoCamera.left * scale;
        orthoCamera.right = orthoCamera.right * scale;
        orthoCamera.top = orthoCamera.top * scale;
        orthoCamera.bottom = orthoCamera.bottom * scale;
        orthoCamera.updateProjectionMatrix();
    }
    graphicStore.extraContext.listeners.signals.onZoomChanged.dispatch(1 / scale);
}

export const drawEntitiesBoundingBox = (models: ModelBase[]) => {
    if (!models || models.length == 0) {
        return;
    }
    let boundingBoxObj = getEntitiesBoundingBox(models, false);
    if (!boundingBoxObj) return;

    let x1 = boundingBoxObj.x1;
    let y1 = boundingBoxObj.y1;
    let z1 = boundingBoxObj.z1;
    let x2 = boundingBoxObj.x2;
    let y2 = boundingBoxObj.y2;
    let z2 = boundingBoxObj.z2;
    let length = Math.abs(x2 - x1);
    let width = Math.abs(y2 - y1);
    let height = Math.abs(z2 - z1);


    // // 创建一个组将包围盒的面和边线都包含进去
    let boundingBoxGroup = new Group();
    let tempBox = new Box(new Point(x1, y1, z1), length, width, height);

    let scaleX = 1;
    let scaleY = 1;
    let scaleZ = 1;
    let w2uMatrix = new Matrix4();
    let u2wMatrix = new Matrix4();

    // 面顶点
    let _verticesBuffer = [];
    tempBox.getMeshVertexBuffer(_verticesBuffer, scaleX, scaleY, scaleZ, w2uMatrix, u2wMatrix);
    // 面颜色
    let _verticeColorsBuffer = [];
    let renderColor = new Color(0x18C8FF);
    for (let i = 0; i < _verticesBuffer?.length; i += 3) {
        _verticeColorsBuffer[i] = renderColor.r;
        _verticeColorsBuffer[i + 1] = renderColor.g;
        _verticeColorsBuffer[i + 2] = renderColor.b;
    }

    // 边顶点
    let _edgeVerticesBuffer = []
    tempBox.getEdgeVertexBuffer(_edgeVerticesBuffer, 0, scaleX, scaleY, scaleZ, w2uMatrix, u2wMatrix);
    // 边颜色
    let _verticeEdgeColorsBuffer = [];
    let renderEdgeColor = new Color(0x1746c2);
    for (let i = 0; i < _edgeVerticesBuffer?.length; i += 3) {
        _verticeEdgeColorsBuffer[i] = renderEdgeColor.r;
        _verticeEdgeColorsBuffer[i + 1] = renderEdgeColor.g;
        _verticeEdgeColorsBuffer[i + 2] = renderEdgeColor.b;
    }

    // 添加面
    let boxMaterial = new MeshBasicMaterial({
        side: DoubleSide,
        vertexColors: true,
        transparent: true,
        opacity: 0.2,
        depthTest: true,
        depthWrite: false,
    });
    const boxGeometry = new BufferGeometry();
    boxGeometry.setAttribute('position', new Float32BufferAttribute(_verticesBuffer, 3));
    boxGeometry.setAttribute('color', new Float32BufferAttribute(_verticeColorsBuffer, 3));
    let boxMesh = new Mesh(boxGeometry, boxMaterial);
    boxMesh.renderOrder = Number.MAX_VALUE;
    boundingBoxGroup.add(boxMesh);

    let edgeMaterial = new LineBasicMaterial({
        vertexColors: true,
        linewidth: 10
    });
    const edgeGeometry = new BufferGeometry();
    edgeGeometry.setAttribute('position', new Float32BufferAttribute(_edgeVerticesBuffer, 3));
    edgeGeometry.setAttribute('color', new Float32BufferAttribute(_verticeEdgeColorsBuffer, 3));
    let edgeMesh = new LineSegments(edgeGeometry, edgeMaterial);
    boundingBoxGroup.add(edgeMesh);

    // 返回包含包围盒的 3D 对象
    return boundingBoxGroup;
}